import APIConfig from "service/network/APIConfig";
import APIService from "service/network/APIService";

/**
 * Allergy v2 API client (api-allergy-v2-service). Same shape as the earlier
 * v2 clients (CC / Dx / Med / Add / Fam / Act).
 *
 * Contract notes:
 *
 * 1. Every mutating call needs `expectedVersion` (the last version the caller
 *    saw). On mismatch the server returns 409 with a fresh doc.
 *
 * 2. Allergen identity fields (name / catalogId / mCode / source) are
 *    preserved server-side on Update — the frontend locks them in the Edit
 *    modal and the server ignores any client-sent value for those.
 *
 * 3. Reactions are stored as `{name, source}` where source ∈ "controlled" |
 *    "free-text". The frontend tags each pill's source at pick time.
 *
 * 4. No status / trend endpoints by design — audit is server-side only for
 *    compliance.
 *
 * 5. Delete is soft — server flips deleted=true; the row disappears from
 *    subsequent list responses.
 */
class AllergyService {
  constructor() {
    this.apiService = new APIService();
  }

  list(patientId, onResponse, onError) {
    const url = APIConfig.allergyV2.list.format(patientId);
    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * Add an allergy. Body shape matches beans.AddRequest:
   *   { name, category, catalogId?, mCode?, source,
   *     reactions: [{name, source}], firstNoted?, lastReaction?,
   *     description?, expectedVersion }
   */
  add(patientId, body, onResponse, onError) {
    const url = APIConfig.allergyV2.add.format(patientId);
    return this.apiService.post(url, body, onResponse, onError);
  }

  /**
   * Full edit — Allergen identity fields are omitted (server preserves them).
   * Body shape matches beans.UpdateRequest:
   *   { category, reactions, firstNoted?, lastReaction?, description?,
   *     expectedVersion }
   */
  update(patientId, allergyId, body, onResponse, onError) {
    const url = APIConfig.allergyV2.update.format(patientId, allergyId);
    return this.apiService.post(url, body, onResponse, onError);
  }

  /**
   * Trend — per-allergy audit history (StatusHistory[] with reporter names
   * enriched server-side). Powers the View trend modal.
   */
  trend(patientId, allergyId, onResponse, onError) {
    const url = APIConfig.allergyV2.trend.format(patientId, allergyId);
    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * Mark one history row as incorrect. `index` is the row's position in
   * StatusHistory as returned by trend(). Audit-only — the live Allergy
   * display is unchanged. Body: { expectedVersion }.
   */
  markHistoryIncorrect(patientId, allergyId, index, expectedVersion, onResponse, onError) {
    const url = APIConfig.allergyV2.markHistoryIncorrect.format(
      patientId,
      allergyId,
      index
    );
    return this.apiService.post(
      url,
      { expectedVersion: expectedVersion || 0 },
      onResponse,
      onError
    );
  }

  /**
   * Soft-delete. Body carries `expectedVersion` for optimistic locking.
   * APIService has no DELETE helper — direct fetch like the other v2 clients.
   */
  delete(patientId, allergyId, expectedVersion, onResponse, onError) {
    const url = APIConfig.allergyV2.delete.format(patientId, allergyId);
    return fetch(url, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        client: this._client(),
        phone_no: this._header("phone_no"),
        auth_token: this._header("auth_token"),
      },
      body: JSON.stringify({ expectedVersion: expectedVersion || 0 }),
    })
      .then((r) => r.json().then((data) => (r.ok ? onResponse(data) : onError(data))))
      .catch((err) =>
        onError({ message: err && err.message ? err.message : "Delete failed" })
      );
  }

  _header(name) {
    try {
      const user = JSON.parse(localStorage.getItem("zy.USER_PROFILE"));
      if (name === "phone_no") return (user && user.phoneno) || "";
      if (name === "auth_token") {
        return (user && user.accessToken && user.accessToken.accessToken) || "";
      }
    } catch (e) {
      /* localStorage unavailable — return empty and let the server 401 */
    }
    return "";
  }

  _client() {
    try {
      const user = JSON.parse(localStorage.getItem("zy.USER_PROFILE"));
      const isDoctor =
        user && user.doctorProfile && user.doctorProfile.id > 0 &&
        !(user.employeeProfile && user.employeeProfile.id > 0);
      return isDoctor ? "doctor_web" : "bridge_web";
    } catch (e) {
      return "bridge_web";
    }
  }
}

export default AllergyService;
